// ============================================================
// TERROR UI v2 — GILDED OBSIDIAN MATERIAL KIT  (July 5, 2026)
// Polished obsidian slabs, forged gold, silver filigree,
// amethyst starlight — dimensional surfaces, not terminal ink.
//
// v1 → v2 (the material revolution):
//   • Every surface is a gradient with a light source (Law 1)
//   • Gold borders are METAL — gradient strips, not 1px hairlines (Law 2)
//   • Interactive elements are faceted (Law 3) and pressable (Law 4)
//   • Active = light: hover blooms, press sinks (Law 5)
//   • ALL keyframes injected imperatively via ensureTerrorKeyframes()
//     — the inline-<style> keyframe trap is dead in this file (Law 6)
//   • Text glows (gold) or sits (body) — never floats (Law 7)
//
// API COMPAT: every v1 export survives with its exact signature.
// NEW primitives: FacetSlab, ChipBadge, CountBadge.
// ============================================================

import { useState } from "react";
import {
  SD, GM,
  GOLD_METAL, GOLD_METAL_H, SILVER_METAL_H,
  OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  facetTB, SHADOW_RAISED, SHADOW_RECESS,
  bloom, textGlow, TEXT_SET,
} from "./theme";

// ─────────────────────────────────────────────────────────────
// KEYFRAMES — injected once to document.head, keyed off DOM node
// existence (the keyframe-trap-safe pattern from HoloFoil).
// Never put <style> blocks inside components in this file.
// ─────────────────────────────────────────────────────────────
function ensureTerrorKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("terror-ui-keyframes")) return;
  const el = document.createElement("style");
  el.id = "terror-ui-keyframes";
  el.textContent = `
    @keyframes twinkle   { 0%,100%{opacity:var(--op)} 50%{opacity:calc(var(--op)*0.1)} }
    @keyframes tShimmer  { 0%{background-position:-200% 0} 100%{background-position:300% 0} }
    @keyframes tBloomPls { 0%,100%{opacity:.75} 50%{opacity:1} }
    @keyframes bhSpin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes bhSpinRev { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
    @keyframes bhPulse   { 0%,100%{opacity:.9;transform:scale(1)} 50%{opacity:.7;transform:scale(1.04)} }
    @keyframes mistFlow  { 0%,100%{transform:translateX(-6%) scaleY(1);opacity:.7} 50%{transform:translateX(6%) scaleY(1.15);opacity:1} }
    @keyframes mistFlow2 { 0%,100%{transform:translateX(8%);opacity:.5} 50%{transform:translateX(-8%);opacity:.85} }
    @keyframes mistFlow3 { 0%,100%{transform:translateX(-4%) translateY(0);opacity:.4} 50%{transform:translateX(4%) translateY(-10px);opacity:.7} }
    @keyframes animaVein { 0%,100%{opacity:.2} 40%{opacity:.55} 70%{opacity:.3} }
    @keyframes islandA   { 0%,100%{transform:translate(0,0)} 40%{transform:translate(12px,-7px)} 70%{transform:translate(-7px,9px)} }
    @keyframes islandB   { 0%,100%{transform:translate(0,0)} 30%{transform:translate(-14px,8px)} 65%{transform:translate(9px,-10px)} }
    @keyframes islandC   { 0%,100%{transform:translate(0,0)} 50%{transform:translate(7px,6px)} }
    @keyframes flowerD   { 0%,100%{transform:translateY(0) rotate(0deg);opacity:.6} 50%{transform:translateY(-16px) rotate(10deg);opacity:1} }
    @keyframes flowerD2  { 0%,100%{transform:translateY(0) rotate(0deg);opacity:.5} 50%{transform:translateY(-10px) rotate(-7deg);opacity:.9} }
    @keyframes goldVein  { 0%,100%{opacity:.07} 50%{opacity:.22} }
    @keyframes crackGlow { 0%,100%{opacity:.04} 50%{opacity:.12} }
    @keyframes voidBrth  { 0%,100%{opacity:.85} 50%{opacity:.6} }
    @keyframes spiralArm { 0%{transform:rotate(0deg) scaleX(1)} 100%{transform:rotate(360deg) scaleX(1)} }
    @keyframes accretePulse { 0%,100%{opacity:.5;transform:scaleX(1)} 50%{opacity:.9;transform:scaleX(1.08)} }
  `;
  document.head.appendChild(el);
}
ensureTerrorKeyframes();

// ─────────────────────────────────────────────────────────────
// RED FLOWER — Terror's flower, now with an inner ember
// ─────────────────────────────────────────────────────────────
export const RedFlower = ({ size = 28, opacity = 0.85, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" style={{ opacity, filter: "drop-shadow(0 0 3px rgba(192,57,43,0.5))", ...style }}>
    <ellipse cx="14" cy="8" rx="3.5" ry="6.5" fill="#8B1A1A" transform="rotate(0 14 14)"/>
    <ellipse cx="14" cy="8" rx="3.5" ry="6.5" fill="#A52020" transform="rotate(45 14 14)"/>
    <ellipse cx="14" cy="8" rx="3.5" ry="6.5" fill="#7A1515" transform="rotate(90 14 14)"/>
    <ellipse cx="14" cy="8" rx="3.5" ry="6.5" fill="#B02A1E" transform="rotate(135 14 14)"/>
    <ellipse cx="14" cy="8" rx="3" ry="5.5" fill="#C0392B" transform="rotate(22 14 14)"/>
    <ellipse cx="14" cy="8" rx="3" ry="5.5" fill="#922020" transform="rotate(67 14 14)"/>
    <ellipse cx="14" cy="8" rx="3" ry="5.5" fill="#C0392B" transform="rotate(112 14 14)"/>
    <ellipse cx="14" cy="8" rx="3" ry="5.5" fill="#8B1A1A" transform="rotate(157 14 14)"/>
    <circle cx="14" cy="14" r="3" fill="#1a0505"/>
    <circle cx="14" cy="14" r="1.5" fill="#5a1010"/>
    <circle cx="13" cy="13" r="0.5" fill="#c8a84b" opacity="0.8"/>
    <circle cx="15" cy="13" r="0.5" fill="#c8a84b" opacity="0.8"/>
    <circle cx="14" cy="15" r="0.5" fill="#c8a84b" opacity="0.7"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────
// CORNER FLOURISH v2 — forged metal filigree. Dual-stroke lines
// (bright over dark = light running along the metal), a silver
// under-scroll, an amethyst star, and the red flower kept — DNA.
// ─────────────────────────────────────────────────────────────
export const CornerFlourish = ({ size = 52, color = GM.inkGold, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 52 52" style={{ filter: `drop-shadow(0 0 6px ${color}55)`, ...style }}>
    {/* main frame lines — dark base under bright core = metal */}
    <line x1="2" y1="2" x2="30" y2="2" stroke="#6b4310" strokeWidth="2.4" opacity="0.9"/>
    <line x1="2" y1="2" x2="2" y2="30" stroke="#6b4310" strokeWidth="2.4" opacity="0.9"/>
    <line x1="2" y1="2" x2="30" y2="2" stroke="#f5d878" strokeWidth="1" opacity="0.95"/>
    <line x1="2" y1="2" x2="2" y2="30" stroke="#f5d878" strokeWidth="1" opacity="0.95"/>
    {/* silver under-scroll — the secondary metal */}
    <line x1="6" y1="6" x2="22" y2="6" stroke={GM.silver} strokeWidth="0.6" opacity="0.5"/>
    <line x1="6" y1="6" x2="6" y2="22" stroke={GM.silver} strokeWidth="0.6" opacity="0.5"/>
    {/* filigree curls */}
    <path d="M8,8 Q16,8 18,16 Q20,22 27,25" stroke={color} strokeWidth="1" fill="none" opacity="0.9"/>
    <path d="M8,8 Q16,8 18,16 Q20,22 27,25" stroke="#f5d878" strokeWidth="0.4" fill="none" opacity="0.7"/>
    <path d="M8,13 Q13,13 14,19 Q15,23 19,25" stroke={GM.silver} strokeWidth="0.5" fill="none" opacity="0.55"/>
    <path d="M13,8 Q13,13 19,14 Q23,15 25,19" stroke={GM.silver} strokeWidth="0.5" fill="none" opacity="0.55"/>
    <path d="M4,26 Q4,4 26,4" stroke={color} strokeWidth="0.5" fill="none" opacity="0.35"/>
    {/* set gems */}
    <polygon points="2,2 4.5,0 7,2 4.5,4.5" fill="#f5d878" opacity="0.98"/>
    <polygon points="15,2 17,0.5 19,2 17,3.5" fill={color} opacity="0.8"/>
    <polygon points="2,15 0.5,17 2,19 3.5,17" fill={color} opacity="0.8"/>
    <polygon points="23,23 25,21 27,23 25,25" fill={GM.amethystBright} opacity="0.75"/>
    <circle cx="11" cy="2" r="0.9" fill="#f5d878" opacity="0.7"/>
    <circle cx="22" cy="2" r="0.9" fill={color} opacity="0.65"/>
    <circle cx="2" cy="11" r="0.9" fill="#f5d878" opacity="0.7"/>
    <circle cx="2" cy="22" r="0.9" fill={color} opacity="0.65"/>
    {/* amethyst star — the Unstable World watching */}
    <path d="M20 18 L20.7 19.9 L22.6 20.6 L20.7 21.3 L20 23.2 L19.3 21.3 L17.4 20.6 L19.3 19.9 Z" fill={GM.amethystBright} opacity="0.7"/>
    <path d="M7,2.5 Q8,2 9,2.5 Q10,3 11,2.5 Q12,2 13,2.5 Q14,3 15,2.5" stroke={color} strokeWidth="0.4" fill="none" opacity="0.5"/>
    <path d="M2.5,7 Q2,8 2.5,9 Q3,10 2.5,11 Q2,12 2.5,13 Q3,14 2.5,15" stroke={color} strokeWidth="0.4" fill="none" opacity="0.5"/>
    {/* the red flower — Terror DNA, kept and glowing */}
    <ellipse cx="5" cy="3" rx="2.8" ry="1.6" fill="#8B1A1A" opacity="0.95" transform="rotate(-30 5 3)"/>
    <ellipse cx="3" cy="5" rx="1.6" ry="2.8" fill="#C0392B" opacity="0.85" transform="rotate(-30 3 5)"/>
    <ellipse cx="7.5" cy="2" rx="2.2" ry="1.3" fill="#A52020" opacity="0.75" transform="rotate(15 7.5 2)"/>
    <ellipse cx="2" cy="7.5" rx="1.3" ry="2.2" fill="#922020" opacity="0.75" transform="rotate(15 2 7.5)"/>
    <circle cx="4" cy="4" r="1.4" fill="#140303"/>
    <circle cx="4" cy="4" r="0.7" fill="#5a1010"/>
    <circle cx="4" cy="4" r="0.3" fill="#f5d878" opacity="0.7"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────
// ORNATE PANEL v2 — a polished obsidian slab in a forged frame.
// Radial-lit surface, 2px metal strips (light runs along them),
// ambient amethyst inner glow, deep float shadow. API unchanged.
// ─────────────────────────────────────────────────────────────
export function OrnatePanel({ children, style = {}, cornerSize = 52, borderColor = GM.inkGold, glowColor = null }) {
  const glow = glowColor || borderColor;
  return (
    <div style={{
      position: "relative",
      background: "radial-gradient(ellipse at 30% 0%, #251b44 0%, #140d26 48%, #0a0616 100%)",
      border: `1px solid ${borderColor}55`,
      borderRadius: 3,
      padding: "32px 28px",
      boxShadow: `0 0 0 1px #06030e, 0 12px 38px rgba(0,0,0,0.75), 0 0 44px ${glow}1c, inset 0 1px 0 rgba(245,216,120,0.22), inset 0 0 70px rgba(139,92,246,0.07)`,
      ...style,
    }}>
      <NoiseOverlay opacity={0.05} />
      <GlassCracks opacity={0.06} />
      {/* crystalline sheen — diagonal facet lines in the glass */}
      <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(135deg, transparent 0px, transparent 24px, rgba(192,132,252,0.035) 24px, rgba(192,132,252,0.035) 25px)", pointerEvents: "none", zIndex: 0, borderRadius: "inherit" }} />
      <div style={{ position: "absolute", inset: 10, border: `1px solid ${borderColor}22`, borderRadius: 1, pointerEvents: "none", zIndex: 1 }} />
      {/* metal strips — 2px, bright core over dark, light runs along them */}
      <div style={{ position: "absolute", top: 5, left: cornerSize - 4, right: cornerSize - 4, height: 2, background: GOLD_METAL_H, opacity: 0.95, zIndex: 1 }} />
      <div style={{ position: "absolute", bottom: 5, left: cornerSize - 4, right: cornerSize - 4, height: 2, background: GOLD_METAL_H, opacity: 0.8, zIndex: 1 }} />
      <div style={{ position: "absolute", left: 5, top: cornerSize - 4, bottom: cornerSize - 4, width: 2, background: `linear-gradient(180deg, transparent, #a06a1e 12%, #e8b84b 35%, #f5d878 50%, #e8b84b 65%, #a06a1e 88%, transparent)`, opacity: 0.85, zIndex: 1 }} />
      <div style={{ position: "absolute", right: 5, top: cornerSize - 4, bottom: cornerSize - 4, width: 2, background: `linear-gradient(180deg, transparent, #a06a1e 12%, #e8b84b 35%, #f5d878 50%, #e8b84b 65%, #a06a1e 88%, transparent)`, opacity: 0.85, zIndex: 1 }} />
      {/* silver echo hairlines just inside the gold */}
      <div style={{ position: "absolute", top: 8, left: cornerSize, right: cornerSize, height: 1, background: SILVER_METAL_H, opacity: 0.22, zIndex: 1 }} />
      <div style={{ position: "absolute", bottom: 8, left: cornerSize, right: cornerSize, height: 1, background: SILVER_METAL_H, opacity: 0.18, zIndex: 1 }} />
      <div style={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}><CornerFlourish size={cornerSize} color={borderColor} /></div>
      <div style={{ position: "absolute", top: 0, right: 0, zIndex: 2, transform: "scaleX(-1)" }}><CornerFlourish size={cornerSize} color={borderColor} /></div>
      <div style={{ position: "absolute", bottom: 0, left: 0, zIndex: 2, transform: "scaleY(-1)" }}><CornerFlourish size={cornerSize} color={borderColor} /></div>
      <div style={{ position: "absolute", bottom: 0, right: 0, zIndex: 2, transform: "scale(-1)" }}><CornerFlourish size={cornerSize} color={borderColor} /></div>
      <div style={{ position: "relative", zIndex: 3 }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ORNATE DIVIDER v2 — a forged rule with a lit gem at center.
// ─────────────────────────────────────────────────────────────
export function OrnateDivider({ color = GM.inkGold, style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0", ...style }}>
      <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${color}44 40%, #f5d878cc)`, boxShadow: `0 0 6px ${color}33` }} />
      <svg width="22" height="22" viewBox="0 0 22 22" style={{ flexShrink: 0, filter: `drop-shadow(0 0 4px ${color}88)` }}>
        <polygon points="11,2 13.2,8.8 20,8.8 14.4,13 16.6,20 11,15.4 5.4,20 7.6,13 2,8.8 8.8,8.8" fill={color} opacity="0.95"/>
        <polygon points="11,5 12.6,9.6 17,9.6 13.6,12.4 14.8,17 11,14.2 7.2,17 8.4,12.4 5,9.6 9.4,9.6" fill="#0a0616" opacity="0.8"/>
        <polygon points="11,7.5 12,10.2 14.6,10.2 12.6,12 13.2,14.6 11,13 8.8,14.6 9.4,12 7.4,10.2 10,10.2" fill={GM.amethystBright} opacity="0.85"/>
      </svg>
      <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, #f5d878cc, ${color}44 60%, transparent)`, boxShadow: `0 0 6px ${color}33` }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TERROR BUTTON v2 — a faceted gem set in a socket. Pressable:
// specular top edge, hover blooms, press sinks. API unchanged.
// (clip-path clips box-shadow → outer depth is drop-shadow.)
// ─────────────────────────────────────────────────────────────
export function TerrorButton({ children, onClick, disabled = false, variant = "gold", style = {}, fullWidth = false }) {
  const [hov, setHov] = useState(false);
  const [prs, setPrs] = useState(false);
  const variants = {
    gold:    { bg: OBSIDIAN_SLAB_LIT, edge: "#f5d878", glow: GM.inkGoldBright,   color: "#ffe9a8", bar: GOLD_METAL },
    teal:    { bg: OBSIDIAN_SLAB,     edge: GM.silverBright, glow: SD.tealLight, color: GM.vellum, bar: SILVER_METAL_H },
    crimson: { bg: OBSIDIAN_SLAB,     edge: "#e05555", glow: "#dc2626",          color: "#e88585", bar: "linear-gradient(160deg,#e05555,#5a0d0d,#c04040)" },
    ghost:   { bg: "transparent",     edge: GM.inkBrown, glow: GM.amethyst,      color: GM.vellumFaint, bar: null },
  };
  const v = variants[variant] || variants.gold;
  const active = hov && !disabled;
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setHov(false); setPrs(false); }}
      onMouseDown={() => setPrs(true)}
      onMouseUp={() => setPrs(false)}
      style={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
        padding: "13px 24px",
        background: disabled ? OBSIDIAN_RECESS : v.bg,
        border: "none",
        clipPath: facetTB(9),
        cursor: disabled ? "not-allowed" : "pointer",
        color: disabled ? GM.vellumFaint : v.color,
        fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 700, letterSpacing: 2,
        opacity: disabled ? 0.4 : 1,
        transform: prs && !disabled ? "translateY(1px)" : "translateY(0)",
        transition: "all 0.18s ease",
        boxShadow: disabled
          ? SHADOW_RECESS
          : `inset 0 1px 0 ${v.edge}${active ? "99" : "55"}, inset 0 -6px 14px rgba(0,0,0,0.45)${active ? `, ${bloom(v.glow)}` : `, inset 0 0 14px ${v.glow}14`}`,
        filter: disabled ? "none" : `drop-shadow(0 3px 8px rgba(0,0,0,0.6))${active ? ` drop-shadow(0 0 10px ${v.glow}55)` : ""}`,
        textShadow: disabled ? "none" : (active ? textGlow(v.glow) : TEXT_SET),
        ...style,
      }}>
      {v.bar && !disabled && (
        <span style={{ position: "absolute", left: 0, top: "16%", bottom: "16%", width: 3, background: v.bar, opacity: active ? 1 : 0.65, boxShadow: active ? `0 0 8px ${v.glow}cc` : "none", transition: "all 0.18s ease" }} />
      )}
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// FACET SLAB (NEW) — the house row/tile: an obsidian shard that
// lights up when active. The menu-row primitive from the mock.
//   <FacetSlab active={sel} onClick={...} accent={GM.inkGoldBright}>
// ─────────────────────────────────────────────────────────────
export function FacetSlab({ children, active = false, onClick, accent = GM.inkGoldBright, cut = 10, style = {} }) {
  const [hov, setHov] = useState(false);
  const lit = active || hov;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        clipPath: facetTB(cut),
        background: active ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
        boxShadow: active
          ? `inset 0 1px 0 #f5d87880, inset 0 0 26px ${GM.amethyst}40, inset 0 -8px 18px rgba(0,0,0,0.4)`
          : `${SHADOW_RAISED}${hov ? `, inset 0 0 18px ${accent}1a` : ""}`,
        filter: `drop-shadow(0 4px 12px rgba(0,0,0,0.65))${lit ? ` drop-shadow(0 0 9px ${accent}44)` : ""}`,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.18s ease",
        borderTop: active ? "1px solid #f5d878" : "1px solid transparent",
        ...style,
      }}>
      {active && (
        <span style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: 3, background: GOLD_METAL, boxShadow: `0 0 8px ${accent}e0`, animation: "tBloomPls 2.4s ease-in-out infinite" }} />
      )}
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CHIP BADGE (NEW) — gem-set pill for currencies/status.
//   <ChipBadge icon="◆" color={GM.inkGoldBright}>1,500</ChipBadge>
// Numbers render in GM.fontUI — legible at any size.
// ─────────────────────────────────────────────────────────────
export function ChipBadge({ icon = null, color = GM.inkGoldBright, children, style = {} }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 20,
      background: OBSIDIAN_RECESS,
      border: `1px solid ${color}88`,
      boxShadow: `${SHADOW_RECESS}, 0 0 10px ${color}22`,
      ...style,
    }}>
      {icon && <span style={{ color, fontSize: 11, textShadow: `0 0 6px ${color}` }}>{icon}</span>}
      <span style={{ color: GM.vellum, fontSize: 12, fontWeight: 700, fontFamily: GM.fontUI, textShadow: TEXT_SET, letterSpacing: 0.4 }}>{children}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// COUNT BADGE (NEW) — the circular ×N gem pinned to card tiles.
//   <CountBadge count={3} color={GM.inkGoldBright} />
// ─────────────────────────────────────────────────────────────
export function CountBadge({ count, color = GM.inkGoldBright, size = 22, style = {} }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: `radial-gradient(circle at 35% 30%, #fff3c8, ${color} 55%, #8a5c18 100%)`,
      color: "#140d02", fontSize: Math.round(size * 0.5), fontWeight: 800,
      fontFamily: GM.fontUI,
      boxShadow: `0 2px 6px rgba(0,0,0,0.7), 0 0 8px ${color}55, inset 0 -2px 4px rgba(0,0,0,0.35)`,
      border: "1px solid rgba(0,0,0,0.5)",
      ...style,
    }}>{count}</span>
  );
}

// ─────────────────────────────────────────────────────────────
// NOISE OVERLAY — polished-glass grain
// ─────────────────────────────────────────────────────────────
export function NoiseOverlay({ opacity = 0.04, style = {} }) {
  return (
    <div style={{
      position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
      backgroundSize: "200px 200px", opacity, mixBlendMode: "overlay", borderRadius: "inherit", ...style,
    }} />
  );
}

// ─────────────────────────────────────────────────────────────
// GLASS CRACKS — the fractured dimension, amethyst-veined
// ─────────────────────────────────────────────────────────────
export function GlassCracks({ opacity = 0.07, style = {} }) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: "inherit", zIndex: 0, ...style }}>
      <svg width="100%" height="100%" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" style={{ opacity }}>
        <line x1="150" y1="120" x2="40"  y2="20"  stroke="#e8f4f8" strokeWidth="0.9"/>
        <line x1="150" y1="120" x2="260" y2="15"  stroke="#e8f4f8" strokeWidth="0.7"/>
        <line x1="150" y1="120" x2="10"  y2="140" stroke="#e8f4f8" strokeWidth="0.8"/>
        <line x1="150" y1="120" x2="290" y2="160" stroke="#e8f4f8" strokeWidth="0.6"/>
        <line x1="150" y1="120" x2="80"  y2="310" stroke="#e8f4f8" strokeWidth="0.7"/>
        <line x1="150" y1="120" x2="205" y2="330" stroke="#e8f4f8" strokeWidth="0.8"/>
        <line x1="150" y1="120" x2="150" y2="400" stroke="#e8f4f8" strokeWidth="0.5"/>
        <line x1="80"  y1="60"  x2="30"  y2="115" stroke="#e8f4f8" strokeWidth="0.45"/>
        <line x1="220" y1="55"  x2="275" y2="105" stroke="#e8f4f8" strokeWidth="0.45"/>
        <line x1="55"  y1="200" x2="15"  y2="250" stroke="#e8f4f8" strokeWidth="0.3"/>
        <circle cx="150" cy="120" r="6"  fill="none" stroke="#e8f4f8" strokeWidth="0.7" opacity="0.65"/>
        <circle cx="150" cy="120" r="14" fill="none" stroke="#e8f4f8" strokeWidth="0.4" opacity="0.35"/>
        <circle cx="150" cy="120" r="25" fill="none" stroke="#e8f4f8" strokeWidth="0.25" opacity="0.2"/>
        <line x1="150" y1="120" x2="40"  y2="20"  stroke="#f5d878" strokeWidth="0.35" opacity="0.4"/>
        <line x1="150" y1="120" x2="205" y2="330" stroke="#f5d878" strokeWidth="0.3" opacity="0.3"/>
        <line x1="150" y1="120" x2="10"  y2="140" stroke="#c084fc" strokeWidth="0.3" opacity="0.3"/>
        <line x1="150" y1="120" x2="290" y2="160" stroke="#c084fc" strokeWidth="0.25" opacity="0.22"/>
      </svg>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STAR DUST — amethyst-leaning drift (rose motes are rare gifts)
// ─────────────────────────────────────────────────────────────
export function StarDust({ count = 40, style = {} }) {
  const stars = Array.from({ length: count }, (_, i) => ({
    x: (i * 37.3 + i * i * 2.1) % 100,
    y: (i * 53.7 + i * 6.9) % 100,
    size: i % 7 === 0 ? 2.5 : i % 3 === 0 ? 1.5 : 1,
    opacity: 0.1 + (i % 7) * 0.065,
    color: i % 9 === 0 ? "#e8b84b" : i % 5 === 0 ? "#c084fc" : i % 11 === 0 ? "#f472b6" : i % 4 === 0 ? "#b8c0d4" : "#e8f4f8",
    dur: 2 + (i % 6) * 0.9,
    delay: (i * 0.4) % 7,
  }));
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0, ...style }}>
      {stars.map((s, i) => (
        <div key={i} style={{
          position: "absolute", left: `${s.x}%`, top: `${s.y}%`,
          width: s.size, height: s.size, borderRadius: "50%",
          background: s.color, opacity: s.opacity, "--op": s.opacity,
          boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
          animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SECTION HEADER v2 — engraved gold, forged rules, set text
// ─────────────────────────────────────────────────────────────
export function TerrorHeader({ children, color = GM.inkGold, size = 11, style = {} }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, ...style }}>
      <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${color}33 55%, #f5d878aa)`, boxShadow: `0 0 5px ${color}2a` }} />
      <div style={{ fontSize: size, fontWeight: 700, color, letterSpacing: 4, fontFamily: "'Cinzel', serif", textTransform: "uppercase", textShadow: `0 0 9px ${color}66, 0 1px 2px #000` }}>{children}</div>
      <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, #f5d878aa, ${color}33 45%, transparent)`, boxShadow: `0 0 5px ${color}2a` }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// UNSTABLE WORLD BG — Black holes, red mist rivers, the void
// (visuals preserved verbatim from v1; keyframes now live in
// ensureTerrorKeyframes — the inline-<style> trap is closed)
// ─────────────────────────────────────────────────────────────
export function UnstableWorldBG() {
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0, pointerEvents: "none" }}>

      {/* ── STAR FIELD — purple tinted, dense ── */}
      {Array.from({ length: 100 }, (_, i) => (
        <div key={`st${i}`} style={{
          position: "absolute",
          left: `${(i * 37.3 + i * i * 2.1) % 100}%`,
          top: `${(i * 53.7 + i * 6.9) % 100}%`,
          width: i % 11 === 0 ? 3 : i % 5 === 0 ? 2 : 1,
          height: i % 11 === 0 ? 3 : i % 5 === 0 ? 2 : 1,
          borderRadius: "50%",
          background: i % 9 === 0 ? "#c8a84b" : i % 6 === 0 ? "#a78bfa" : i % 13 === 0 ? "#f87171" : "#e8f4f8",
          opacity: 0.08 + (i % 8) * 0.06,
          "--op": 0.08 + (i % 8) * 0.06,
          animation: `twinkle ${1.8 + (i % 7) * 0.7}s ease-in-out ${(i * 0.28) % 6}s infinite`,
        }} />
      ))}

      {/* ══════════════════════════════════════════════════════
          BLACK HOLE 1 — top left, large purple
      ══════════════════════════════════════════════════════ */}
      <div style={{ position: "absolute", top: "-8%", left: "-5%", width: 380, height: 380 }}>
        {/* Outer accretion disk — spiral arms */}
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0%, rgba(109,40,217,0.35) 15%, transparent 30%, rgba(109,40,217,0.25) 50%, transparent 65%, rgba(167,139,250,0.3) 80%, transparent 100%)", animation: "bhSpin 18s linear infinite", filter: "blur(8px)" }} />
        {/* Inner hot ring */}
        <div style={{ position: "absolute", inset: "18%", borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0%, rgba(167,139,250,0.6) 12%, transparent 25%, rgba(109,40,217,0.5) 45%, transparent 60%, rgba(196,140,250,0.55) 78%, transparent 100%)", animation: "bhSpinRev 11s linear infinite", filter: "blur(4px)" }} />
        {/* Event horizon glow */}
        <div style={{ position: "absolute", inset: "30%", borderRadius: "50%", background: "radial-gradient(circle, rgba(109,40,217,0.7) 0%, rgba(45,27,105,0.9) 45%, #000001 70%)", animation: "bhPulse 6s ease-in-out infinite" }} />
        {/* True singularity */}
        <div style={{ position: "absolute", inset: "41%", borderRadius: "50%", background: "radial-gradient(circle, #000001 60%, rgba(109,40,217,0.4) 100%)" }} />
        {/* Gravitational lensing ring */}
        <div style={{ position: "absolute", inset: "27%", borderRadius: "50%", border: "1px solid rgba(167,139,250,0.4)", boxShadow: "0 0 20px rgba(109,40,217,0.5), inset 0 0 20px rgba(109,40,217,0.3)" }} />
        {/* Outer glow halo */}
        <div style={{ position: "absolute", inset: "-20%", borderRadius: "50%", background: "radial-gradient(circle, transparent 35%, rgba(109,40,217,0.12) 50%, rgba(109,40,217,0.06) 65%, transparent 80%)", animation: "bhPulse 8s ease-in-out infinite 2s" }} />
      </div>

      {/* ══════════════════════════════════════════════════════
          BLACK HOLE 2 — bottom right, medium crimson/red
      ══════════════════════════════════════════════════════ */}
      <div style={{ position: "absolute", bottom: "-5%", right: "-4%", width: 280, height: 280 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0%, rgba(220,38,38,0.4) 18%, transparent 35%, rgba(153,27,27,0.3) 55%, transparent 70%, rgba(220,38,38,0.35) 85%, transparent 100%)", animation: "bhSpinRev 22s linear infinite", filter: "blur(7px)" }} />
        <div style={{ position: "absolute", inset: "16%", borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0%, rgba(239,68,68,0.55) 14%, transparent 28%, rgba(220,38,38,0.45) 50%, transparent 65%, rgba(239,68,68,0.5) 80%, transparent 100%)", animation: "bhSpin 13s linear infinite", filter: "blur(3px)" }} />
        <div style={{ position: "absolute", inset: "29%", borderRadius: "50%", background: "radial-gradient(circle, rgba(220,38,38,0.65) 0%, rgba(127,29,29,0.9) 45%, #000001 70%)", animation: "bhPulse 7s ease-in-out infinite 1s" }} />
        <div style={{ position: "absolute", inset: "40%", borderRadius: "50%", background: "radial-gradient(circle, #000001 60%, rgba(220,38,38,0.35) 100%)" }} />
        <div style={{ position: "absolute", inset: "26%", borderRadius: "50%", border: "1px solid rgba(239,68,68,0.35)", boxShadow: "0 0 18px rgba(220,38,38,0.45), inset 0 0 18px rgba(220,38,38,0.25)" }} />
        <div style={{ position: "absolute", inset: "-22%", borderRadius: "50%", background: "radial-gradient(circle, transparent 35%, rgba(153,27,27,0.1) 50%, rgba(153,27,27,0.05) 65%, transparent 80%)", animation: "bhPulse 9s ease-in-out infinite 3s" }} />
      </div>

      {/* ══════════════════════════════════════════════════════
          BLACK HOLE 3 — top right, small teal/gold
      ══════════════════════════════════════════════════════ */}
      <div style={{ position: "absolute", top: "5%", right: "8%", width: 150, height: 150, opacity: 0.7 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0%, rgba(14,116,144,0.45) 20%, transparent 40%, rgba(34,211,238,0.3) 60%, transparent 80%, rgba(14,116,144,0.4) 95%, transparent 100%)", animation: "bhSpin 28s linear infinite", filter: "blur(5px)" }} />
        <div style={{ position: "absolute", inset: "22%", borderRadius: "50%", background: "radial-gradient(circle, rgba(14,116,144,0.6) 0%, rgba(10,40,48,0.9) 50%, #000001 70%)", animation: "bhPulse 5s ease-in-out infinite 4s" }} />
        <div style={{ position: "absolute", inset: "36%", borderRadius: "50%", background: "radial-gradient(circle, #000001 60%, rgba(14,116,144,0.3) 100%)" }} />
        <div style={{ position: "absolute", inset: "19%", borderRadius: "50%", border: "1px solid rgba(34,211,238,0.3)", boxShadow: "0 0 12px rgba(14,116,144,0.4)" }} />
      </div>

      {/* ══════════════════════════════════════════════════════
          RED MIST RIVERS — flowing crimson through the void
      ══════════════════════════════════════════════════════ */}
      {/* River 1 — main diagonal river, bright */}
      <div style={{ position: "absolute", top: "30%", left: "-20%", width: "140%", height: "22%", background: "radial-gradient(ellipse at 40% 50%, rgba(220,38,38,0.28) 0%, rgba(153,27,27,0.18) 30%, transparent 65%)", animation: "mistFlow 11s ease-in-out infinite", filter: "blur(6px)", transform: "rotate(-8deg)" }} />
      {/* River 2 — lower river */}
      <div style={{ position: "absolute", top: "55%", left: "-15%", width: "130%", height: "18%", background: "radial-gradient(ellipse at 55% 50%, rgba(185,28,28,0.22) 0%, rgba(127,29,29,0.14) 35%, transparent 65%)", animation: "mistFlow2 14s ease-in-out infinite 3s", filter: "blur(8px)", transform: "rotate(5deg)" }} />
      {/* River 3 — thin upper vein */}
      <div style={{ position: "absolute", top: "15%", left: "10%", width: "110%", height: "12%", background: "radial-gradient(ellipse at 60% 50%, rgba(239,68,68,0.16) 0%, rgba(153,27,27,0.1) 40%, transparent 65%)", animation: "mistFlow3 17s ease-in-out infinite 6s", filter: "blur(10px)", transform: "rotate(-3deg)" }} />
      {/* River 4 — vertical edge river right */}
      <div style={{ position: "absolute", top: "20%", right: "-5%", width: "25%", height: "60%", background: "radial-gradient(ellipse at 80% 50%, rgba(220,38,38,0.2) 0%, transparent 60%)", animation: "animaVein 9s ease-in-out infinite 2s", filter: "blur(5px)" }} />
      {/* River 5 — vertical edge left */}
      <div style={{ position: "absolute", top: "10%", left: "-3%", width: "22%", height: "55%", background: "radial-gradient(ellipse at 20% 50%, rgba(153,27,27,0.22) 0%, transparent 60%)", animation: "animaVein 12s ease-in-out infinite", filter: "blur(6px)" }} />
      {/* Mist tendrils — SVG paths */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", animation: "animaVein 10s ease-in-out infinite" }} viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
        <path d="M0,220 Q40,200 25,320 Q12,400 0,480" stroke="rgba(220,38,38,0.25)" strokeWidth="3" fill="none" filter="url(#blur)"/>
        <path d="M400,160 Q360,210 375,330 Q388,410 400,510" stroke="rgba(153,27,27,0.2)" strokeWidth="2.5" fill="none"/>
        <path d="M0,350 Q60,330 45,440 Q30,510 55,600" stroke="rgba(185,28,28,0.15)" strokeWidth="2" fill="none"/>
        <path d="M400,300 Q340,280 360,390 Q375,460 350,560" stroke="rgba(239,68,68,0.18)" strokeWidth="2" fill="none"/>
        <path d="M150,0 Q170,60 140,140 Q120,200 160,300" stroke="rgba(220,38,38,0.12)" strokeWidth="1.5" fill="none"/>
      </svg>

      {/* ══════════════════════════════════════════════════════
          PURPLE LIGHT SWIRLS — gravitational wash
      ══════════════════════════════════════════════════════ */}
      <div style={{ position: "absolute", top: "10%", left: "5%", width: "55%", height: "50%", background: "radial-gradient(ellipse, rgba(109,40,217,0.18) 0%, rgba(45,27,105,0.1) 40%, transparent 70%)", filter: "blur(30px)", animation: "bhPulse 15s ease-in-out infinite" }} />
      <div style={{ position: "absolute", top: "40%", right: "5%", width: "40%", height: "40%", background: "radial-gradient(ellipse, rgba(167,139,250,0.12) 0%, transparent 65%)", filter: "blur(25px)", animation: "bhPulse 20s ease-in-out infinite 7s" }} />
      <div style={{ position: "absolute", bottom: "20%", left: "20%", width: "35%", height: "30%", background: "radial-gradient(ellipse, rgba(109,40,217,0.14) 0%, transparent 65%)", filter: "blur(22px)", animation: "bhPulse 12s ease-in-out infinite 4s" }} />

      {/* ── Floating Islands ── */}
      <div style={{ position: "absolute", top: "18%", left: "12%", animation: "islandA 22s ease-in-out infinite" }}>
        <svg width="180" height="55" viewBox="0 0 180 55">
          <ellipse cx="90" cy="50" rx="88" ry="11" fill="#030210" opacity="0.95"/>
          <path d="M5,50 Q22,24 48,15 Q72,7 90,11 Q112,4 138,17 Q158,26 175,50 Z" fill="#07051a"/>
          <path d="M5,50 Q22,24 48,15 Q72,7 90,11 Q112,4 138,17 Q158,26 175,50" fill="none" stroke="#a78bfa" strokeWidth="0.7" opacity="0.45"/>
          <circle cx="48" cy="15" r="1.3" fill="#c8a84b" opacity="0.7"/>
          <circle cx="90" cy="11" r="1.6" fill="#c8a84b" opacity="0.8"/>
          <circle cx="138" cy="17" r="1.3" fill="#c8a84b" opacity="0.7"/>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "42%", right: "3%", animation: "islandB 28s ease-in-out infinite 6s" }}>
        <svg width="145" height="44" viewBox="0 0 145 44">
          <ellipse cx="72" cy="39" rx="70" ry="9" fill="#030210" opacity="0.88"/>
          <path d="M5,39 Q18,18 38,11 Q56,4 72,8 Q90,2 110,13 Q128,21 140,39 Z" fill="#07051a"/>
          <path d="M5,39 Q18,18 38,11 Q56,4 72,8 Q90,2 110,13 Q128,21 140,39" fill="none" stroke="#dc2626" strokeWidth="0.6" opacity="0.35"/>
          <circle cx="72" cy="8" r="1.2" fill="#c8a84b" opacity="0.65"/>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "62%", left: "3%", animation: "islandC 19s ease-in-out infinite 4s" }}>
        <svg width="115" height="34" viewBox="0 0 115 34">
          <ellipse cx="57" cy="30" rx="55" ry="7" fill="#030210" opacity="0.82"/>
          <path d="M4,30 Q16,12 34,7 Q50,2 57,5 Q68,0 84,8 Q99,15 111,30 Z" fill="#07051a"/>
          <path d="M4,30 Q16,12 34,7 Q50,2 57,5 Q68,0 84,8 Q99,15 111,30" fill="none" stroke="#c8a84b" strokeWidth="0.5" opacity="0.4"/>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "3%", left: "35%", animation: "islandB 35s ease-in-out infinite 15s", opacity: 0.4 }}>
        <svg width="75" height="20" viewBox="0 0 75 20">
          <path d="M2,16 Q14,4 32,1 Q48,0 57,3 Q68,5 73,16 Z" fill="#07051a"/>
          <path d="M2,16 Q14,4 32,1 Q48,0 57,3 Q68,5 73,16" fill="none" stroke="#c8a84b" strokeWidth="0.4" opacity="0.3"/>
        </svg>
      </div>

      {/* ── Red Flowers drifting ── */}
      <div style={{ position: "absolute", top: "22%", left: "18%", animation: "flowerD 14s ease-in-out infinite" }}><RedFlower size={15} opacity={0.55} /></div>
      <div style={{ position: "absolute", top: "28%", right: "20%", animation: "flowerD2 17s ease-in-out infinite 3s" }}><RedFlower size={11} opacity={0.45} /></div>
      <div style={{ position: "absolute", top: "52%", left: "6%", animation: "flowerD 20s ease-in-out infinite 8s" }}><RedFlower size={13} opacity={0.5} /></div>
      <div style={{ position: "absolute", top: "46%", right: "6%", animation: "flowerD2 16s ease-in-out infinite 2s" }}><RedFlower size={18} opacity={0.38} /></div>
      <div style={{ position: "absolute", top: "8%", right: "32%", animation: "flowerD 22s ease-in-out infinite 11s" }}><RedFlower size={8} opacity={0.3} /></div>
      <div style={{ position: "absolute", bottom: "34%", left: "28%", animation: "flowerD2 18s ease-in-out infinite 5s" }}><RedFlower size={12} opacity={0.44} /></div>
      <div style={{ position: "absolute", bottom: "12%", right: "40%", animation: "flowerD 15s ease-in-out infinite 9s" }}><RedFlower size={9} opacity={0.32} /></div>

      {/* ── Gold veins ── */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", animation: "goldVein 14s ease-in-out infinite" }} viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
        <path d="M110,0 Q130,110 100,220 Q80,320 120,430 Q150,510 110,640" stroke="#c8a84b" strokeWidth="0.7" fill="none" opacity="0.16"/>
        <path d="M290,60 Q270,160 300,270 Q320,360 280,470" stroke="#c8a84b" strokeWidth="0.55" fill="none" opacity="0.11"/>
        <path d="M200,0 Q215,90 195,180 Q178,260 210,340" stroke="#c8a84b" strokeWidth="0.4" fill="none" opacity="0.08"/>
      </svg>

      {/* ── Shattered space cracks ── */}
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", animation: "crackGlow 9s ease-in-out infinite" }} viewBox="0 0 400 800" preserveAspectRatio="xMidYMid slice">
        <line x1="0"   y1="130" x2="85"  y2="210" stroke="#e8f4f8" strokeWidth="0.5" opacity="0.15"/>
        <line x1="85"  y1="210" x2="45"  y2="330" stroke="#e8f4f8" strokeWidth="0.35" opacity="0.1"/>
        <line x1="400" y1="90"  x2="315" y2="195" stroke="#e8f4f8" strokeWidth="0.5" opacity="0.12"/>
        <line x1="315" y1="195" x2="355" y2="315" stroke="#e8f4f8" strokeWidth="0.35" opacity="0.08"/>
        <line x1="185" y1="0"   x2="235" y2="125" stroke="#e8f4f8" strokeWidth="0.4" opacity="0.1"/>
        <line x1="0"   y1="520" x2="65"  y2="420" stroke="#e8f4f8" strokeWidth="0.3" opacity="0.07"/>
        <line x1="400" y1="620" x2="335" y2="520" stroke="#e8f4f8" strokeWidth="0.3" opacity="0.07"/>
        <line x1="200" y1="400" x2="160" y2="500" stroke="#a78bfa" strokeWidth="0.3" opacity="0.12"/>
        <line x1="200" y1="400" x2="250" y2="480" stroke="#a78bfa" strokeWidth="0.25" opacity="0.1"/>
      </svg>

      {/* ── Mysterious shapes ── */}
      <div style={{ position: "absolute", top: "35%", right: "16%", animation: "bhSpin 32s linear infinite" }}>
        <svg width="38" height="38" viewBox="0 0 38 38" style={{ opacity: 0.1 }}>
          <polygon points="19,3 35,33 3,33" fill="none" stroke="#a78bfa" strokeWidth="1"/>
          <polygon points="19,9 30,31 8,31" fill="none" stroke="#6d28d9" strokeWidth="0.6"/>
          <circle cx="19" cy="19" r="3.5" fill="none" stroke="#c8a84b" strokeWidth="0.5"/>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "68%", left: "12%", animation: "bhSpinRev 40s linear infinite" }}>
        <svg width="28" height="28" viewBox="0 0 28 28" style={{ opacity: 0.09 }}>
          <rect x="6" y="6" width="16" height="16" fill="none" stroke="#dc2626" strokeWidth="0.8" transform="rotate(45 14 14)"/>
          <rect x="10" y="10" width="8" height="8" fill="none" stroke="#c8a84b" strokeWidth="0.5" transform="rotate(45 14 14)"/>
        </svg>
      </div>
      <div style={{ position: "absolute", top: "80%", right: "22%", animation: "bhSpin 26s linear infinite 8s" }}>
        <svg width="22" height="22" viewBox="0 0 22 22" style={{ opacity: 0.08 }}>
          <polygon points="11,1 21,11 11,21 1,11" fill="none" stroke="#22d3ee" strokeWidth="0.7"/>
          <polygon points="11,5 17,11 11,17 5,11" fill="none" stroke="#a78bfa" strokeWidth="0.45"/>
        </svg>
      </div>

      {/* ── Arena center glow ── */}
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "80%", height: "60%", borderRadius: "50%", background: "radial-gradient(ellipse at 50% 50%, rgba(45,27,105,0.28) 0%, transparent 65%)", animation: "voidBrth 8s ease-in-out infinite" }} />
    </div>
  );
}
